// D18's archived banner (DS row 81, `banner.neutral`; DC-209 "Archived is the same page, read-only, found through the
// same lists"): *Archived {date} · searchable, reportable, auditable, reopenable*, the snapshot's content hash in mono,
// and Reopen for a seat the server admits. Reopen is a host callback: the host opens B2's reopen dialog (or, on the
// archive search page, the existing reopen request), so this banner never writes. His (CR-060): "Filled/Cancelled
// archive immediately and remain searchable/reportable/auditable/reopenable." Composes D0 only; the host wraps it in
// McRoot when the page is a V1 one.

import { Banner, Button, IdPill, formatDate } from "@/mc";
import { ARCHIVED_ON, ARCHIVE_LABELS } from "./labels";

export interface ArchivedBannerProps {
  requisitionId: string;
  /** The archive instant (S07's `archived_at`, or the snapshot's `taken_at`). */
  archivedAt: string;
  /** The snapshot's content hash; empty when the reader does not carry it, and then not drawn. */
  contentHash: string;
  /** The server's `can` for a reopen request on this requisition. */
  canReopen: boolean;
  /** Opens the host's reopen dialog. Without it there is no Reopen, whatever canReopen says. */
  onReopen?: (() => void) | undefined;
}

export function ArchivedBanner({ requisitionId, archivedAt, contentHash, canReopen, onReopen }: ArchivedBannerProps) {
  const reopen = canReopen && onReopen ? (
    <Button sm onClick={onReopen} >{ARCHIVE_LABELS.reopen}</Button>
  ) : null;
  return (
    <div data-requisition-id={requisitionId} data-archived="true">
      <Banner tone="neutral" right={reopen}>
        {ARCHIVED_ON({ date: formatDate(archivedAt) })}
        {contentHash ? <> <IdPill value={contentHash} can={false} /></> : null}
      </Banner>
    </div>
  );
}

/**
 * The read-only state (D9: read-only `requisition.open`, `RECORD_ARCHIVED` on writes): on an archived record every write
 * flag of a `can` map reads false, so the host draws no write control. Reopen is the one way back and stays as the server
 * set it; the server refuses any other write on an archived root whatever the screen shows.
 */
export function archivedCan<T extends Readonly<Record<string, boolean | null | undefined>>>(can: T, archived: boolean): T {
  if (!archived) return can;
  const out: Record<string, boolean | null | undefined> = {};
  for (const [key, value] of Object.entries(can)) out[key] = READ_ONLY_KEEPS.has(key) ? value : false;
  return out as T;
}

/** The `can` keys an archived record keeps: asking to reopen it (B2's `request_reopen`, V1's `reopen`). */
const READ_ONLY_KEEPS = new Set(["reopen", "request_reopen"]);

/** True for a record whose archive state is archived (the `state_archive` column, or the archived status). */
export function isArchived(r: { state_archive?: string | null; status?: string | null } | null | undefined): boolean {
  return r?.state_archive === "archived" || r?.status === "archived";
}
